import './App.css';
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { distance } from './scripts/distance';
import { foods as staticFoods } from './scripts/foods';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faLinkedin, faXTwitter, faSquareReddit } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpFromBracket, faHeart, faArrowRightFromBracket, faLocationDot, faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import FoodGrid from './scripts/FoodGrid';
import RestaurantCard from './scripts/RestaurantCard';
import MyFavoriteRestaurants from './scripts/MyFavoriteRestaurants';
import AdSense from './scripts/AdSense';
import Cookies from 'js-cookie';
import { useGoogleLogin } from '@react-oauth/google';

const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

function App() {
    const [selectedFood, setSelectedFood]     = useState(null);
    const [restaurants, setRestaurants]       = useState([]);
    const [isSearching, setIsSearching]       = useState(false);
    const [searchError, setSearchError]       = useState(null);
    const [targetCount, setTargetCount]       = useState(0);
    const [cachedCoords, setCachedCoords]     = useState(null);
    const [location, setLocation]             = useState('');
    const [locationDetecting, setLocationDetecting] = useState(false);
    const [selectedDistance, setSelectedDistance]   = useState(distance[2]);
    const [foodHistory, setFoodHistory]       = useState([]);
    const [user, setUser]                     = useState(null);
    const [accessToken, setAccessToken]       = useState(null);
    const [isMenuOpen, setIsMenuOpen]         = useState(false);
    const [favoritesMode, setFavoritesMode]   = useState(false);
    const [myFavorites, setMyFavorites]       = useState([]);
    const [favoritesShownNum, setFavoritesShownNum] = useState(10);
    const [restaurantIds, setRestaurantIds]   = useState([]);
    const [restaurantJsons, setRestaurantJsons] = useState([]);
    const [dynamicFoods, setDynamicFoods]     = useState(null);
    const [foodsLoading, setFoodsLoading]     = useState(false);
    const [shownCount, setShownCount]         = useState(5);
    const menuRef = useRef(null);
    const lastFoodLocRef = useRef('');
    const foodDebounceTimerRef = useRef(null);
    const gpsRef = useRef(null); // raw GPS coords — used for distance calc regardless of typed location
    const apiKey = process.env.REACT_APP_YTSEARCH_KEY;
    const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    const anthropicKey = process.env.REACT_APP_ANTHROPIC_API_KEY;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadFoodsForLocation = async (loc, userTriggered = false) => {
        const normalizedLoc = (loc || 'global').toLowerCase().trim().replace(/\s+/g, ' ');
        const cacheKey  = `cachedFoodsListv8_${normalizedLoc}`;
        const dateKey   = `cachedFoodsDatev8_${normalizedLoc}`;
        // Always show cached version immediately (avoids blank flash while loading)
        const cached     = localStorage.getItem(cacheKey);
        const cachedDate = localStorage.getItem(dateKey);
        if (cached) { try { setDynamicFoods(JSON.parse(cached)); } catch (e) {} }
        // Skip fresh fetch only on initial page load when cache is still fresh (< 24h)
        if (!userTriggered && cached && cachedDate && Date.now() - parseInt(cachedDate, 10) < 86400000) return;
        if (!anthropicKey) return;
        setFoodsLoading(true);
        try {
            const month = new Date().toLocaleString('default', { month: 'long' });
            const recentFoods = foodHistory.slice(0, 5).map(h => h.foodName).filter(Boolean);
            const userContent = [
                loc ? `Location: ${loc}` : 'Location: global',
                `Month: ${month}`,
                recentFoods.length > 0 ? `Recently searched (avoid repeating): ${recentFoods.join(', ')}` : '',
            ].filter(Boolean).join('\n');

            const res = await fetch('https://api.anthropic.com/v1/messages', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': anthropicKey,
                    'anthropic-version': '2023-06-01',
                    'anthropic-dangerous-direct-browser-access': 'true',
                },
                body: JSON.stringify({
                    model: 'claude-haiku-4-5-20251001',
                    max_tokens: 2048,
                    system: `You are a local food discovery guide. Given a location and month, list exactly 24 foods and dishes that are genuinely popular or currently trending there. Think like a local — include iconic regional specialties, neighborhood favorites, and dishes people actually line up for.

Rules:
- Lead with the most location-specific items (e.g. Bay Area → Dim Sum, Boba Tea, Ramen, Korean BBQ, Banh Mi, Pho; Austin → BBQ Brisket, Breakfast Tacos, Tex-Mex; NYC → Bagels, Pizza Slice, Cheesecake)
- Use specific dish names when possible ("Dim Sum" not "Chinese", "Boba Tea" not "Tea", "Pho" not "Vietnamese")
- Reflect the real local food culture — ethnic cuisines dominant in that area should appear prominently
- Factor in the month for seasonal foods (e.g. hot pot and ramen in winter, shaved ice and cold noodles in summer, pumpkin spice and apple cider in fall)
- The first 12 items must be the most iconic and popular foods for this location regardless of search history — these are the "staples" users expect to see
- The last 12 items should avoid the "Recently searched" foods to surface variety the user hasn't tried yet
- Do NOT produce a generic global list

Respond with ONLY a raw JSON array, no markdown fences, no explanation. Each element:
{"name":"...","emoji":"...","nameKeywords":["..."]}
where:
- nameKeywords: specific terms likely in a restaurant's name that specializes in this dish (e.g. for "Boba Tea": ["Boba","Bubble Tea","Milk Tea"]; for "Ramen": ["Ramen"]; for "Pho": ["Pho"])`,
                    messages: [{ role: 'user', content: userContent }],
                }),
            });
            const data = await res.json();
            if (!data.content?.[0]?.text) throw new Error(JSON.stringify(data));
            // Strip markdown code fences if Claude wraps the response
            let text = data.content[0].text.trim();
            text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
            const parsed = JSON.parse(text);
            const withIds = parsed.map((f, i) => ({ ...f, id: i }));
            localStorage.setItem(cacheKey, JSON.stringify(withIds));
            localStorage.setItem(dateKey, Date.now().toString());
            setDynamicFoods(withIds);
        } catch (err) {
            console.error('Failed to load dynamic foods:', err);
        } finally {
            setFoodsLoading(false);
        }
    };

    const handleLoginSuccess = (response) => {
        const token = response.access_token;
        setAccessToken(token);
        fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.json())
            .then(data => {
                const userInfo = { email: data.email, picture: data.picture, name: data.name };
                setUser(userInfo);
                localStorage.setItem('user', JSON.stringify(userInfo));
            })
            .catch(err => console.error('Error fetching user data:', err));
    };

    const login = useGoogleLogin({
        clientId: clientId,
        scope: 'email profile',
        prompt: 'consent',
        onSuccess: handleLoginSuccess,
        onError: () => console.log('Login Failed'),
    });

    const logout = () => {
        setUser(null);
        localStorage.removeItem('user');
        setIsMenuOpen(false);
        if (favoritesMode) favoritesModelRemove();
        if (accessToken) {
            fetch(`https://accounts.google.com/o/oauth2/revoke?token=${accessToken}`, {
                method: 'POST',
                headers: { 'Content-type': 'application/x-www-form-urlencoded' },
            }).catch(err => console.error('Error revoking token:', err));
        }
    };

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) setUser(JSON.parse(savedUser));
        const locationCookie = Cookies.get('locationCookie');
        if (locationCookie) setLocation(locationCookie);
        else detectLocation();
        const distanceIdCookie = Cookies.get('distanceIdCookie');
        if (distanceIdCookie !== undefined) setSelectedDistance(distance[distanceIdCookie] || distance[2]);
        const foodHistoryCookie = Cookies.get('foodHistoryCookie');
        if (foodHistoryCookie) { try { setFoodHistory(JSON.parse(foodHistoryCookie)); } catch(e){} }
        // Silently fetch GPS on load only to pre-geocode if the cookie is a plain city name
        // Do NOT store GPS coords under an arbitrary locationCookie key — if the user is
        // physically in a different place than the saved location (e.g. locationCookie="中壢夜市"
        // but GPS is in Fremont), it would corrupt cachedCoords and search the wrong area.
        if (navigator.permissions && navigator.geolocation) {
            navigator.permissions.query({ name: 'geolocation' }).then(result => {
                if (result.state === 'granted') {
                    navigator.geolocation.getCurrentPosition(
                        async ({ coords: { latitude, longitude } }) => {
                            gpsRef.current = { lat: latitude, lon: longitude };
                            // Only cache if reverse-geocoded city matches the saved location cookie
                            try {
                                const res = await fetch(
                                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`,
                                    { referrerPolicy: 'unsafe-url' }
                                );
                                const data = await res.json();
                                const comps = data.results?.[0]?.address_components || [];
                                const getComp = (type) => comps.find(c => c.types.includes(type))?.long_name || '';
                                const city = getComp('locality') || getComp('sublocality') || getComp('administrative_area_level_2');
                                if (locationCookie && city && locationCookie.toLowerCase().includes(city.toLowerCase())) {
                                    setCachedCoords({ query: locationCookie, lat: latitude, lon: longitude, fromGPS: true });
                                }
                            } catch (e) {}
                        },
                        () => {},
                        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
                    );
                }
            }).catch(() => {});
        }
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && event.target.className !== 'Profile_Icon') {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        Cookies.set('distanceIdCookie', selectedDistance.id, { expires: 365 });
    }, [selectedDistance]);

    useEffect(() => {
        Cookies.set('foodHistoryCookie', JSON.stringify(foodHistory), { expires: 365 });
    }, [foodHistory]);

    useEffect(() => {
        const loc = location.trim();
        if (!loc) return;
        if (foodDebounceTimerRef.current) clearTimeout(foodDebounceTimerRef.current);
        foodDebounceTimerRef.current = setTimeout(() => {
            if (loc === lastFoodLocRef.current) return;
            lastFoodLocRef.current = loc;
            loadFoodsForLocation(loc, true);
        }, 800);
        return () => { if (foodDebounceTimerRef.current) clearTimeout(foodDebounceTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const handleIdsCollected = (id, restaurantJson) => {
        if (!restaurantIds.includes(id)) {
            restaurantIds.push(id);
            restaurantJsons.push(restaurantJson);
            checkLikeCount();
            checkClickCount();
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    async function checkLikeCount() {
        const total = favoritesMode ? favoritesShownNum : targetCount;
        if (total === 0 || restaurantIds.length !== total) return;
        const results = await likeCountAPI(restaurantIds);
        if (!results) return;
        for (const result of results) {
            const el = document.getElementById(result._id);
            if (el) el.innerText = result.count;
        }
        if (isMenuOpen) setIsMenuOpen(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    async function checkClickCount() {
        const total = favoritesMode ? favoritesShownNum : targetCount;
        if (total === 0 || restaurantIds.length !== total) return;
        const results = await clickCountAPI(restaurantIds);
        if (!results) return;
        for (const result of results) {
            const el = document.getElementById(`c_${result._id}`);
            if (el) el.innerText = result.count;
        }
    }

    const likeCountAPI = async (ids) => {
        try {
            const response = await axios.post('https://ytsearch.kenylin.com/api/mdb/likerestaurantcountbybatch', { restaurant_ids: ids, key: apiKey });
            return response.data.counts;
        } catch (err) { console.log(err.message); }
    };

    const clickCountAPI = async (ids) => {
        try {
            const response = await axios.post('https://ytsearch.kenylin.com/api/mdb/clickrestaurantcountbybatch', { restaurant_ids: ids, key: apiKey });
            return response.data.counts;
        } catch (err) { console.log(err.message); }
    };

    const handleLocationChange = (e) => {
        setLocation(e.target.value);
        Cookies.set('locationCookie', e.target.value, { expires: 365 });
        if (selectedFood || favoritesMode) {
            setSelectedFood(null);
            setRestaurants([]);
            setSearchError(null);
            setFavoritesMode(false);
            setMyFavorites([]);
        }
    };

    const detectLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser.');
            return;
        }
        setLocationDetecting(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                gpsRef.current = { lat: latitude, lon: longitude };
                try {
                    const res = await fetch(
                        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`,
                        { referrerPolicy: 'unsafe-url' }
                    );
                    const data = await res.json();
                    const comps = data.results?.[0]?.address_components || [];
                    const getComp = (type, nameType = 'long_name') => comps.find(c => c.types.includes(type))?.[nameType] || '';
                    const city        = getComp('locality') || getComp('sublocality') || getComp('administrative_area_level_2');
                    const state       = getComp('administrative_area_level_1', 'short_name');
                    const countryCode = getComp('country', 'short_name');
                    const country     = getComp('country');
                    const region = ['US', 'CA', 'AU'].includes(countryCode) ? state : country;
                    const locationStr = city ? `${city}, ${region}` : region;
                    setLocation(locationStr);
                    Cookies.set('locationCookie', locationStr, { expires: 365 });
                    setCachedCoords({ query: locationStr, lat: latitude, lon: longitude, fromGPS: true });
                } catch (err) {
                    console.error('Reverse geocode failed:', err);
                    const coordStr = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
                    setLocation(coordStr);
                    Cookies.set('locationCookie', coordStr, { expires: 365 });
                    setCachedCoords({ query: coordStr, lat: latitude, lon: longitude, fromGPS: true });
                }
                setSelectedFood(null);
                setRestaurants([]);
                setSearchError(null);
                setLocationDetecting(false);
            },
            (err) => {
                console.error('Geolocation error:', err);
                alert('Could not detect your location. Please enter it manually.');
                setLocationDetecting(false);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    const searchRestaurants = async (food) => {
        const loc = location.trim();
        if (!loc) { alert('Please enter your location first.'); return; }

        setSelectedFood(food);
        setIsSearching(true);
        setRestaurants([]);
        setSearchError(null);
        setRestaurantIds([]);
        setRestaurantJsons([]);
        setTargetCount(0);
        setShownCount(5);

        // Track food history (most recent first, deduplicated)
        setFoodHistory(prev => [{ foodId: food.id, foodName: food.name }, ...prev.filter(h => h.foodId !== food.id)].slice(0, 10));

        try {
            // GPS path: use coords captured via the detect button (fromGPS flag).
            // Typed path: geocode with Nominatim to get the OSM area ID (relation/way),
            //   then use area(ID) in Overpass — a direct indexed lookup, not a slow regex scan.
            const useGPSCoords = !!(cachedCoords?.fromGPS && cachedCoords?.query === loc);
            let lat = null, lon = null;

            if (useGPSCoords) {
                ({ lat, lon } = cachedCoords);
            } else if (cachedCoords?.query === loc && !cachedCoords.fromGPS) {
                ({ lat, lon } = cachedCoords);
            } else {
                // Geocode via Places Text Search (header-auth avoids website-restriction issues)
                const gpKey = process.env.REACT_APP_GOOGLE_PLACES_KEY;
                const geoRes = await fetch('https://places.googleapis.com/v1/places:searchText', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': gpKey,
                        'X-Goog-FieldMask': 'places.location,places.formattedAddress',
                    },
                    body: JSON.stringify({ textQuery: loc, maxResultCount: 1 }),
                });
                const geoData = await geoRes.json();
                const place = geoData.places?.[0];
                if (!place?.location) {
                    setSearchError('Location not found. Try a different city or place name.');
                    setIsSearching(false);
                    setSelectedFood(null);
                    return;
                }
                lat = place.location.latitude;
                lon = place.location.longitude;
                console.log(`Geocoded "${loc}" → ${place.formattedAddress}`);
                setCachedCoords({ query: loc, lat, lon, fromGPS: false });
            }

            // If GPS is available and the user is near their searched location (< 20 mi),
            // replace the Nominatim centroid with the precise GPS position.
            // Zip code centroids are often in marshlands/water; GPS gives accurate results.
            if (!useGPSCoords && gpsRef.current && lat != null && lon != null) {
                const gpsDist = haversineDistance(gpsRef.current.lat, gpsRef.current.lon, lat, lon);
                if (gpsDist < 20) {
                    lat = gpsRef.current.lat;
                    lon = gpsRef.current.lon;
                }
            }

            // Search restaurants via Google Places Text Search
            const radius = selectedDistance.meters;
            let results = [];
            let gpConnected = false;

            // --- Google Places ---
            {
                const gpKey = process.env.REACT_APP_GOOGLE_PLACES_KEY;
                // Build a focused query: food name + location hint for better relevance
                const gpQuery = `${food.name} restaurant`;
                const gpCacheKey = `gp11_${Math.round(lat * 100)}_${Math.round(lon * 100)}_${radius}_${food.name}`;
                let gpData = null;
                const cachedGp = sessionStorage.getItem(gpCacheKey);
                if (cachedGp) {
                    try { gpData = JSON.parse(cachedGp); gpConnected = true; console.log('Google Places: cache hit'); } catch (_) {}
                }
                if (!gpData) {
                    const ctrl = new AbortController();
                    const t = setTimeout(() => ctrl.abort(), 15000);
                    try {
                        const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Goog-Api-Key': gpKey,
                                'X-Goog-FieldMask': 'places.id,places.displayName,places.formattedAddress,places.location,places.nationalPhoneNumber,places.websiteUri,places.currentOpeningHours,places.rating,places.userRatingCount',
                            },
                            body: JSON.stringify({
                                textQuery: gpQuery,
                                locationBias: {
                                    circle: {
                                        center: { latitude: lat, longitude: lon },
                                        radius: radius,
                                    },
                                },
                                maxResultCount: 20,
                            }),
                            signal: ctrl.signal,
                        });
                        clearTimeout(t);
                        if (res.ok) {
                            const j = await res.json();
                            gpData = j.places || [];
                            gpConnected = true;
                            console.log(`Google Places: ${gpData.length} results`);
                            try { sessionStorage.setItem(gpCacheKey, JSON.stringify(gpData)); } catch (_) {}
                        } else {
                            console.warn(`Google Places HTTP ${res.status}:`, await res.text());
                        }
                    } catch (e) {
                        clearTimeout(t);
                        if (e.name !== 'AbortError') console.warn('Google Places failed:', e.message);
                    }
                }
                if (gpData?.length) {
                    results = gpData
                        .filter(p => p.displayName?.text)
                        .map(place => ({
                            id: `gp-${place.id}`,
                            name: place.displayName.text,
                            cuisine: food.name,
                            address: place.formattedAddress || '',
                            website: place.websiteUri || null,
                            phone: place.nationalPhoneNumber || null,
                            opening_hours: place.currentOpeningHours?.weekdayDescriptions?.join('\n') || null,
                            stars: place.rating || null,
                            ratingCount: place.userRatingCount || null,
                            distanceMiles: haversineDistance(lat, lon, place.location.latitude, place.location.longitude),
                            restaurantImage: null,
                            lat: place.location.latitude,
                            lon: place.location.longitude,
                            foodEmoji: food.emoji,
                            foodName: food.name,
                        }))
                        .sort((a, b) => (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity))
                        .slice(0, 20);
                }
            }

            if (!results.length) {
                setSearchError(
                    !gpConnected
                        ? 'Could not reach Google Places. Check your connection and try again.'
                        : `No ${food.name} spots found ${selectedDistance.name.toLowerCase()}. Try increasing the distance or a different location.`
                );
            } else {
                // Fetch one food photo from TheMealDB for this food type (cached per session)
                let foodImage = null;
                const mealKey = `meal_photo_${food.name}`;
                const mealCached = sessionStorage.getItem(mealKey);
                if (mealCached) {
                    foodImage = mealCached;
                } else {
                    try {
                        const mealRes = await fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(food.name)}`);
                        const mealData = await mealRes.json();
                        const thumb = mealData.meals?.[0]?.strMealThumb;
                        if (thumb) { sessionStorage.setItem(mealKey, thumb); foodImage = thumb; }
                    } catch (e) {}
                }
                const withPhoto = results.map(r => ({ ...r, foodImage: foodImage || null }));
                setRestaurants(withPhoto);
                setTargetCount(Math.min(5, withPhoto.length));

                // Background: reverse-geocode restaurants missing street addresses
                (async () => {
                    const toGeocode = withPhoto.filter(r => !r.address && r.lat != null && r.lon != null);
                    for (const r of toGeocode) {
                        try {
                            await new Promise(res => setTimeout(res, 150));
                            const gRes = await fetch(
                                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${r.lat},${r.lon}&key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}`,
                                { referrerPolicy: 'unsafe-url' }
                            );
                            const gData = await gRes.json();
                            const comps = gData.results?.[0]?.address_components || [];
                            const getComp = (type, nameType = 'long_name') => comps.find(c => c.types.includes(type))?.[nameType] || '';
                            const street = [getComp('street_number'), getComp('route')].filter(Boolean).join(' ');
                            const city   = getComp('locality') || getComp('sublocality') || getComp('administrative_area_level_2');
                            const state  = getComp('administrative_area_level_1', 'short_name');
                            const post   = getComp('postal_code');
                            const addr   = [street, city, state, post].filter(Boolean).join(', ');
                            if (addr) setRestaurants(prev => prev.map(x => x.id === r.id ? { ...x, address: addr } : x));
                        } catch (e) {}
                    }
                })();
            }
        } catch (err) {
            console.error('Search failed:', err);
            setSearchError('Search failed. Please check your connection and try again.');
        }
        setIsSearching(false);
    };

    const restaurantClick = (event) => {
        let restaurantId   = '';
        let restaurantName = '';

        if (event.target.tagName === 'A') {
            restaurantId   = event.target.getAttribute('restaurantid');
            restaurantName = event.target.getAttribute('restaurantname');
            if (!restaurantId) return;
        } else if (
            (event.target.tagName === 'svg' && event.target.parentElement.tagName === 'P') ||
            (event.target.tagName === 'path' && event.target.parentElement.parentElement.tagName === 'P')
        ) {
            const likeObj = event.target.tagName === 'svg' ? event.target : event.target.parentElement;
            if (likeObj.getAttribute('clicked') === 'true') return;
            restaurantId   = likeObj.getAttribute('restaurantid');
            restaurantName = likeObj.getAttribute('restaurantname');
            likeObj.setAttribute('clicked', 'true');
            callLikeAPI(restaurantId, restaurantName).then(() => {});
            return;
        } else if (event.target.tagName === 'svg' && event.target.parentElement.tagName === 'A') {
            restaurantId   = event.target.parentElement.getAttribute('restaurantid');
            restaurantName = event.target.parentElement.getAttribute('restaurantname');
        } else if (event.target.tagName === 'path' && event.target.parentElement.parentElement.tagName === 'A') {
            restaurantId   = event.target.parentElement.parentElement.getAttribute('restaurantid');
            restaurantName = event.target.parentElement.parentElement.getAttribute('restaurantname');
        } else {
            return;
        }

        if (!restaurantId) return;
        const clickCount = document.getElementById(`c_${restaurantId}`);
        if (clickCount) clickCount.innerText = (parseInt(clickCount.innerText, 10) + 1).toString();
        callClickAPI(restaurantId, restaurantName).then(() => {});
    };

    const callLikeAPI = async (restaurantId, restaurantName) => {
        const rJson = restaurantJsons.find(r => r?.id === restaurantId);
        const jsonToSave = {
            restaurant_id:   restaurantId,
            restaurant_name: restaurantName,
            food_name:       selectedFood?.name || rJson?.foodName || '',
            user:            !user ? 'guest' : user.email,
            restaurant_json: rJson,
            key:             apiKey,
        };
        try {
            const response = await axios.post('https://ytsearch.kenylin.com/api/mdb/likerestaurant', jsonToSave);
            if (response.status === 201) {
                const likeCount = document.getElementById(restaurantId);
                if (likeCount) likeCount.innerText = (parseInt(likeCount.innerText, 10) + 1).toString();
            }
        } catch (error) {
            if (error.response?.status !== 409) console.error('Error saving record:', error);
        }
    };

    const callClickAPI = async (restaurantId, restaurantName) => {
        const rJson = restaurantJsons.find(r => r?.id === restaurantId);
        const jsonToSave = {
            restaurant_id:   restaurantId,
            restaurant_name: restaurantName,
            food_name:       selectedFood?.name || rJson?.foodName || '',
            user:            !user ? 'guest' : user.email,
            restaurant_json: rJson,
            key:             apiKey,
        };
        try {
            await axios.post('https://ytsearch.kenylin.com/api/mdb/clickrestaurant', jsonToSave);
        } catch (error) {
            console.error('Error saving record:', error);
        }
    };

    const showMyFavorites = async () => {
        if (!user || isSearching) return;
        try {
            const response = await axios.post('https://ytsearch.kenylin.com/api/mdb/likesbyrestaurantuser', {
                user: user.email,
                key:  apiKey,
            });
            const likes = response.data.likes;
            setRestaurants([]);
            setRestaurantIds([]);
            setRestaurantJsons([]);
            setIsMenuOpen(false);
            setFavoritesMode(true);
            setFavoritesShownNum(10);
            setMyFavorites(likes.filter(like => !!like.restaurant_json));
        } catch (err) {
            console.log(err.message);
        }
    };

    function moreFavoritesNum() {
        setFavoritesShownNum(prev => prev + 10);
    }

    function loadMoreRestaurants() {
        const next = Math.min(shownCount + 5, restaurants.length);
        setShownCount(next);
        setTargetCount(next);
    }

    function favoritesModelRemove() {
        setFavoritesMode(false);
        setMyFavorites([]);
        setRestaurants([]);
        setSelectedFood(null);
    }

    const url  = 'https://food.kenylin.com';
    const text = 'Discover real restaurants with AI!';

    const socialLinks = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        reddit:   `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`,
    };

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(url)
            .then(() => alert('URL copied to clipboard. Share it with friends. Thank you!'))
            .catch(err => console.error('Failed to copy URL:', err));
    };

    return (
        <div className="App">
            <div className="Content">
                <div className="Header" id='header'>
                    <div>
                        <div>food for you</div>
                        <div className="text-sm italic text-gray-400 -mt-0.5">
                            let <span className="text-blue-400 font-extrabold animate-pulse">AI</span> find food for you
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-base">
                        <FontAwesomeIcon onClick={handleCopyUrl} className="cursor-pointer hover:text-gray-400" icon={faArrowUpFromBracket} />
                        <div className="w-px h-4 bg-gray-600" />
                        <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon className="hover:text-[#1877F2]" icon={faFacebook} />
                        </a>
                        <a href={socialLinks.reddit} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon className="hover:text-[#FF4500]" icon={faSquareReddit} />
                        </a>
                        <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon className="hover:text-black hover:bg-white hover:rounded-full" icon={faXTwitter} />
                        </a>
                        <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon className="hover:text-[#0A66C2]" icon={faLinkedin} />
                        </a>
                        <div className="w-px h-4 bg-gray-600" />
                        <div className="relative">
                            {!user ? (
                                <svg onClick={() => login()} className="Profile_Icon cursor-pointer" title="Sign in to Google" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                                </svg>
                            ) : (
                                <img onClick={() => setIsMenuOpen(prev => !prev)} className="Profile_Icon" src={user.picture} alt={user.name} title={user.name} referrerPolicy="no-referrer" />
                            )}
                            {isMenuOpen && (
                                <div ref={menuRef} className="absolute top-10 right-0 z-20 text-left bg-gray-800 w-44 rounded-lg shadow-xl border border-stone-600">
                                    <ul className="py-1">
                                        <li className="px-4 py-2 hover:bg-gray-700 cursor-pointer rounded-t-lg" onClick={() => showMyFavorites()}>
                                            <div className="flex gap-3 items-center text-sm font-semibold"><FontAwesomeIcon icon={faHeart} className="text-orange-400" /><span>my favorites</span></div>
                                        </li>
                                        <li className="px-4 py-2 hover:bg-gray-700 cursor-pointer rounded-b-lg" onClick={() => logout()}>
                                            <div className="flex gap-3 items-center text-sm font-semibold"><FontAwesomeIcon icon={faArrowRightFromBracket} className="text-gray-400" /><span>sign out</span></div>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search bar — location + distance in one row */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-stone-800 border border-stone-700 mb-5">
                    <FontAwesomeIcon icon={faLocationDot} className="text-orange-400 flex-shrink-0" />
                    <input type="text" value={location} onChange={handleLocationChange}
                           placeholder="city or zip code" className="Location_Input" />
                    <button onClick={detectLocation} disabled={locationDetecting}
                            className={`Detect_Button ${locationDetecting} flex-shrink-0`} title="Detect my location">
                        <FontAwesomeIcon icon={faCrosshairs} className={locationDetecting ? 'animate-spin' : ''} />
                        <span className="ml-1 text-xs hidden sm:inline">{locationDetecting ? 'detecting...' : 'detect'}</span>
                    </button>
                    <div className="w-px h-6 bg-stone-600 flex-shrink-0" />
                    <Listbox value={selectedDistance} onChange={setSelectedDistance}>
                        <div className="relative flex-shrink-0">
                            <ListboxButton className="Options_ListBoxButton">
                                <span className="flex items-center">
                                    <span className="ml-1 block truncate text-sm">
                                        <span className="hidden sm:inline">{selectedDistance.name}</span>
                                        <span className="sm:hidden">{selectedDistance.id === 0 ? 'Any' : selectedDistance.name.replace('Within ', '').replace('miles', 'mi').replace('mile', 'mi')}</span>
                                    </span>
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
                                </span>
                            </ListboxButton>
                            <ListboxOptions transition className="Options_ListBoxOptions right-0">
                                {distance.map(d => (
                                    <ListboxOption key={d.id} value={d}
                                                   className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-orange-500 data-[focus]:text-white">
                                        <span className="ml-1 block truncate text-sm group-data-[selected]:font-semibold">{d.name}</span>
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-500 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                                            <CheckIcon aria-hidden="true" className="h-5 w-5" />
                                        </span>
                                    </ListboxOption>
                                ))}
                            </ListboxOptions>
                        </div>
                    </Listbox>
                </div>

                {/* Food grid (default view) */}
                {!selectedFood && !favoritesMode && (
                    <FoodGrid onFoodSelect={searchRestaurants} foodHistory={foodHistory} foods={dynamicFoods || staticFoods} foodsLoading={foodsLoading} location={location} />
                )}

                {/* Results area */}
                <div className="Result">
                    <FontAwesomeIcon icon={faHeart} className="hidden" />

                    {/* Food mode header */}
                    {selectedFood && !favoritesMode && (
                        <div className="flex items-center gap-3 mt-4 mb-2">
                            <button onClick={() => { setSelectedFood(null); setRestaurants([]); setSearchError(null); }} className="Back_Button shrink-0">
                                ← all foods
                            </button>
                            <div className="min-w-0">
                                <p className="text-lg font-bold leading-tight truncate">
                                    {selectedFood.emoji} {selectedFood.name}
                                </p>
                                {location && (
                                    <p className="text-xs text-gray-400 truncate">near {location}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Favorites header */}
                    {favoritesMode && (
                        <div className="flex items-center gap-3 mt-4 mb-2">
                            <button className="Back_Button" onClick={favoritesModelRemove}>← back</button>
                            <span className="text-lg font-bold">❤️ my favorites</span>
                            <span className="text-sm text-gray-400">{myFavorites.length} saved</span>
                        </div>
                    )}

                    {/* Loading */}
                    {isSearching && (
                        <div className="text-center py-12 text-orange-400">
                            <div className="text-4xl mb-3 animate-bounce">{selectedFood?.emoji}</div>
                            <p className="text-sm text-gray-400">searching real restaurants on Google Places...</p>
                        </div>
                    )}

                    {/* Error */}
                    {searchError && !isSearching && (
                        <div className="text-center py-8 text-gray-400 text-sm">{searchError}</div>
                    )}

                    {/* Restaurant list */}
                    {!isSearching && (
                        // eslint-disable-next-line jsx-a11y/no-redundant-roles
                        <ul role="list" className="divide-y divide-orange-900">
                            {restaurants.slice(0, shownCount).map((restaurant, index) => (
                                <React.Fragment key={restaurant.id}>
                                    <li onClick={restaurantClick}>
                                        <RestaurantCard restaurant={restaurant} onIdCollected={handleIdsCollected} />
                                    </li>
                                    {(index + 1) % 5 === 0 && (
                                        <li key={`ad-${index}`} className="border-t border-orange-900 list-none">
                                            <AdSense layout="-h3-21+3a-e+1y" slot="2299454801" />
                                        </li>
                                    )}
                                </React.Fragment>
                            ))}

                            {favoritesMode && myFavorites.length === 0 && (
                                <p className="text-center text-gray-400 py-8">no saved restaurants yet</p>
                            )}

                            {favoritesMode && myFavorites.slice(0, favoritesShownNum).map((fav, index) => (
                                <li key={fav._id} onClick={restaurantClick}>
                                    <MyFavoriteRestaurants restaurant={fav} index={index} onIdCollected={handleIdsCollected} />
                                    {index % 10 === 9 && <div className="border-t border-orange-900"><AdSense layout="-h3-21+3a-e+1y" slot="6836761345" /></div>}
                                    {(index + 1) === favoritesShownNum && (index + 1) < myFavorites.length && (
                                        <div className="border-t border-orange-900 pt-3 text-center">
                                            <button className="More_Button" onClick={moreFavoritesNum}>load more</button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    {!isSearching && !favoritesMode && shownCount < restaurants.length && (
                        <div className="pt-3 pb-2 text-center">
                            <button className="More_Button" onClick={loadMoreRestaurants}>load 5 more</button>
                        </div>
                    )}
                    {!isSearching && selectedFood && !favoritesMode && restaurants.length > 0 && (
                        <div className="pt-3 pb-2">
                            <button onClick={() => { setSelectedFood(null); setRestaurants([]); setSearchError(null); }} className="Back_Button">
                                ← all foods
                            </button>
                        </div>
                    )}
                    {!isSearching && favoritesMode && myFavorites.length > 0 && (
                        <div className="pt-3 pb-2">
                            <button className="Back_Button" onClick={favoritesModelRemove}>← back</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;
